import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { ContactPayload } from '@/features/directories/api/useDirectory'
import {
  counterpartiesPath,
  visibleCounterparties,
  type CounterpartyQuery,
} from '@/features/counterparties/counterpartyList'
import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { MOCK_COUNTERPARTIES } from '@/mocks/directories'
import { USE_MOCKS } from '@/mocks/fixtures'
import type {
  Counterparty,
  CounterpartyCategory,
  CounterpartyRow,
} from '@/types/domain'

/* Counterparties — every company or person that provides us a service, one
 * record with a category (S-22b). `/api/counterparties` replaced the
 * directory's `/api/directories/participants`.
 *
 * Two things the client deliberately does NOT do, because the server does
 * them and a second copy would drift:
 *
 *   - it does not hide rows by role. A reader without `counterparty.read`
 *     receives an EMPTY list (not a 403, so a picker still renders and keeps
 *     its stored value) and a 404 on one record.
 *   - it does not decide who may act. The controls follow `counterpartyRights`
 *     as an affordance; every write is refused server-side regardless.
 */

/** The body a counterparty form submits — create and edit alike. Contacts go
 *  whole, each stored one under its served `id` (see `ContactPayload`): a
 *  contact missing from the list is deleted. Which is why the edit form seeds
 *  from `useCounterparty(id)` and never from a list row, which carries none. */
export interface CounterpartyPayload {
  name: string
  category: CounterpartyCategory
  taxId: string
  registrationId: string
  country: string
  address: string
  contacts: ContactPayload[]
}

/** The list read. The section asks once with `includeInactive` and filters on
 *  the client (the chip counts need every row); a picker passes its categories
 *  and gets the active rows. Every distinct filter is its own cache entry,
 *  because the server answers a different list to each.
 *
 *  `enabled` is for a caller mounted before it needs the list — a merge sheet
 *  rendered closed. */
export function useCounterparties(
  filters: CounterpartyQuery = {},
  { enabled }: { enabled?: boolean } = {},
) {
  const key = {
    categories: filters.categories?.length ? [...filters.categories] : undefined,
    search: filters.search?.trim() || undefined,
    includeInactive: filters.includeInactive || undefined,
  }
  return useQuery<CounterpartyRow[]>({
    queryKey: queryKeys.counterparties.list(key),
    enabled,
    queryFn: async () =>
      USE_MOCKS
        ? mockList(filters)
        : (await api.get<{ items: CounterpartyRow[] }>(counterpartiesPath(filters))).items,
  })
}

/** One counterparty with its contacts — the only read an edit form may seed
 *  from. A 404 covers both a missing record and a reader without
 *  `counterparty.read`, and the page words it as "not available". */
export function useCounterparty(
  id: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery<Counterparty>({
    queryKey: queryKeys.counterparties.detail(id ?? ''),
    queryFn: async () => {
      if (!USE_MOCKS) return api.get<Counterparty>(`/counterparties/${id}`)
      const mock = MOCK_COUNTERPARTIES.find((row) => row.id === id)
      // Thrown, as useClient's mock branch does: react-query rejects an
      // undefined result, and a fixture gap should say which id was missing.
      if (!mock) throw new Error(`no mock counterparty for id ${id}`)
      return mock
    },
    enabled: enabled && Boolean(id),
  })
}

/** The counterparty caches, and the case and quote caches that NAME one: a leg
 *  or a truck shows its carrier's name, a cost line its vendor's, so a rename
 *  or a (de)activation must reach a case page already in the cache. */
function useInvalidateCounterpartyReaders() {
  const queryClient = useQueryClient()
  return () => {
    for (const queryKey of [
      queryKeys.counterparties.all,
      queryKeys.cases.all,
      queryKeys.quotes.all,
    ]) {
      void queryClient.invalidateQueries({ queryKey })
    }
  }
}

export function useCreateCounterparty() {
  const invalidate = useInvalidateCounterpartyReaders()
  return useMutation<Counterparty, Error, CounterpartyPayload>({
    mutationFn: (payload) => api.post<Counterparty>('/counterparties', payload),
    onSuccess: invalidate,
  })
}

export function useUpdateCounterparty() {
  const invalidate = useInvalidateCounterpartyReaders()
  return useMutation<Counterparty, Error, { id: string; payload: CounterpartyPayload }>({
    mutationFn: ({ id, payload }) => api.put<Counterparty>(`/counterparties/${id}`, payload),
    onSuccess: invalidate,
  })
}

export function useSetCounterpartyActive() {
  const invalidate = useInvalidateCounterpartyReaders()
  return useMutation<Counterparty, Error, { id: string; isActive: boolean }>({
    mutationFn: ({ id, isActive }) =>
      api.patch<Counterparty>(`/counterparties/${id}/active`, { isActive }),
    onSuccess: invalidate,
  })
}

/** Deletes a counterparty. Refused with 409 while anything references it, the
 *  refusal NAMING the records (`referencedRefusal`) and suggesting the way
 *  out — merge, or deactivate for one on a debit note. */
export function useDeleteCounterparty() {
  const invalidate = useInvalidateCounterpartyReaders()
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete<void>(`/counterparties/${id}`),
    onSuccess: invalidate,
  })
}

/** Folds one counterparty into another of the same category. The id in the
 *  path is the record KEPT, as with clients.
 *
 *  A merge repoints legs, trucks, cost lines and quote cost lines, so beyond
 *  the readers every write reaches it also refreshes finance — the supplier
 *  balances are keyed by the counterparty a cost line names. Refused 422
 *  `category: mismatched` across categories, and 409 with the notes named when
 *  a debit note references the source (frozen history: deactivate instead). */
export function useMergeCounterparties() {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateCounterpartyReaders()
  return useMutation<void, Error, { targetId: string; sourceId: string }>({
    mutationFn: ({ targetId, sourceId }) =>
      api.post<void>(`/counterparties/${targetId}/merge`, { sourceId }),
    onSuccess: () => {
      invalidate()
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
    },
  })
}

/** The fixture answer to a list read, filtered the way the server filters. */
function mockList({ categories, search, includeInactive }: CounterpartyQuery): CounterpartyRow[] {
  const wanted = categories?.length ? categories : undefined
  const rows = MOCK_COUNTERPARTIES.filter(
    (row) => !wanted || wanted.includes(row.category),
  ).map(({ address: _address, ownerId: _ownerId, contacts: _contacts, ...row }) => row)
  const matching = visibleCounterparties(rows, {
    category: 'all',
    search: search ?? '',
    inactive: includeInactive ?? false,
  })
  // The server lists the active rows first.
  return [...matching.filter((row) => row.isActive), ...matching.filter((row) => !row.isActive)]
}
